import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ChevronDown } from "lucide-react";
import { useState } from "react";

const Privacy = () => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const sections = [
    {
      id: "intro",
      title: "Introduction",
      content: "OptiBiz Suite ('we', 'us', 'our', or 'Company') operates the OptiBizsuite.com website and related applications (the 'Service'). This page informs you of our policies regarding the collection, use, and disclosure of personal data when you use our Service and the choices you have associated with that data. We are committed to protecting your privacy and ensuring you have a positive experience on our platform."
    },
    {
      id: "definitions",
      title: "Definitions",
      content: "Service refers to the website, mobile application, and any related services provided by OptiBiz Suite. Personal Data means any information relating to an identified or identifiable natural person. Usage Data includes information collected automatically when using the Service, such as IP addresses, browser types, pages visited, and time/date stamps. Cookies are small files stored on your device to remember information about your preferences."
    },
    {
      id: "collection",
      title: "Information Collection and Use",
      content: `We collect several types of information for various purposes:

Personal Data: When you create an account, we collect your name, email address, phone number, company name, and any other information you choose to provide. Payment information is processed through secure third-party processors.

Usage Data: IP address, browser type, operating system, pages visited, and access times.

Communication Data: Messages, attachments, and inquiry details when you contact us.`
    },
    {
      id: "usage",
      title: "Use of Data",
      content: `OptiBiz Suite uses collected data to:

• Provide and maintain the Service
• Notify you about changes
• Allow participation in interactive features
• Provide customer support
• Gather analysis and feedback
• Monitor usage and detect technical issues
• Send promotional communications (opt-out available)
• Comply with legal obligations`
    },
    {
      id: "security",
      title: "Security of Data",
      content: `We implement industry-standard security measures including encryption, firewalls, and SSL technology. Access is restricted to authorized personnel. We conduct regular security audits.

However, no method of transmission over the Internet is 100% secure. You are responsible for maintaining login credential confidentiality.`
    },
    {
      id: "retention",
      title: "Data Retention",
      content: `We retain Personal Data only as long as necessary for the stated purposes. Account data is retained while your account is active. Upon deletion request, data is removed within 30 days, except where legal retention is required. Backup copies may be retained temporarily before permanent deletion.`
    },
    {
      id: "rights",
      title: "Your Data Protection Rights",
      content: `You may have rights to:

• Access your data
• Correct inaccurate information
• Request erasure (right to be forgotten)
• Restrict processing
• Request data portability
• Object to processing
• Withdraw consent

Contact info@optibiz.co.ke to exercise these rights.`
    },
    {
      id: "cookies",
      title: "Cookies and Tracking",
      content: `We use cookies for essential functionality, analytics, marketing, and preferences. You can configure your browser to refuse cookies, but this may limit Service functionality.

We use Google Analytics and similar services. You can opt-out using the Google Analytics opt-out browser extension.`
    },
    {
      id: "thirdparty",
      title: "Third-Party Information Sharing",
      content: `We do not sell Personal Data. We share information with:

• Service providers (payment processors, hosting, analytics)
• Business partners (with your consent)
• Legal authorities (when required by law)
• Successor entities (in case of merger/acquisition)

All third parties are bound by confidentiality agreements.`
    },
    {
      id: "international",
      title: "International Data Transfers",
      content: `Your data may be transferred to and processed in Kenya, where data protection laws may differ. By using our Service, you consent to such transfers. We take necessary steps to ensure secure processing in accordance with this Privacy Policy.`
    },
    {
      id: "contact",
      title: "Contact Us",
      content: `For privacy questions, contact:

OptiBiz Suite
Email: info@optibiz.co.ke
Phone: +254 769 653 431
Address: Daima Towers, Eldoret, Uasin Gishu, Kenya

We respond to data privacy inquiries within 30 days.`
    }
  ];

  return (
    <div className="min-h-screen bg-tasktide-bg dark:bg-gray-900 font-sans flex flex-col">
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="pt-32 pb-12 px-4 md:px-8 bg-gradient-to-b from-optibiz-blue/8 to-transparent dark:from-optibiz-blue/10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-extrabold text-tasktide-dark dark:text-white mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-4">
              Your privacy is important to us. Learn how we collect, use, and protect your personal information.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Last updated: December 2025 • Effective from December 1, 2025
            </p>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-20 px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {sections.map((section) => (
                <div
                  key={section.id}
                  className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:border-optibiz-blue/30 dark:hover:border-optibiz-blue/50 transition-colors"
                >
                  <button
                    onClick={() =>
                      setExpandedSection(
                        expandedSection === section.id ? null : section.id
                      )
                    }
                    className="w-full px-6 py-5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
                  >
                    <h2 className="text-xl font-bold text-tasktide-dark dark:text-white">
                      {section.title}
                    </h2>
                    <ChevronDown
                      className={`w-6 h-6 text-optibiz-blue transition-transform duration-300 ${
                        expandedSection === section.id ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {expandedSection === section.id && (
                    <div className="px-6 pb-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                      <p className="text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                        {section.content}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* CTA Box */}
            <div className="mt-12 bg-optibiz-blue/10 dark:bg-optibiz-blue/20 rounded-xl p-8 border border-optibiz-blue/20 dark:border-optibiz-blue/30">
              <h3 className="text-2xl font-bold text-tasktide-dark dark:text-white mb-4">
                Questions About Our Privacy Practices?
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                We're committed to transparency and protecting your privacy rights. Contact our privacy team with any concerns.
              </p>
              <a
                href="mailto:info@optibiz.co.ke"
                className="inline-block bg-optibiz-blue text-white px-8 py-3 rounded-lg font-semibold hover:bg-optibiz-blue/90 transition-all"
              >
                Contact Privacy Team
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Privacy;
